import { useAtom } from '@reatom/npm-react'
import { memo, useMemo } from 'react'

import { tickerItems } from '../../model/ticker-strip'
import { percent, usd } from '../portfolio-screen/money'
import { TokenMark } from '../portfolio-screen/token-mark'

import { reatomTickerFeed } from './ticker-feed'

import type { Network } from '@root/entities/network'

/**
 * The strip of other tokens, in the board's own bar.
 *
 * Pulse and Portfolio both put a forty-pixel bar directly under the nav, and
 * this screen had nothing there. Both terminals we are measured against run a
 * ticker in exactly that slot, so the slot we already have is where it goes —
 * no new piece of chrome, and it inherits the bar's behaviour at narrow widths
 * for free.
 */
export const TradeBar: React.FC<{
  network: Network.Network
  pairAddress?: string
}> = memo(({ network, pairAddress }) => {
  // eslint-disable-next-line @reatom/unit-naming-rule
  const feed = useMemo(() => reatomTickerFeed({ network }), [network])
  const [rows] = useAtom((ctx) => ctx.spy(feed).value ?? [], [feed])

  const items = useMemo(
    () => tickerItems(rows, { exclude: pairAddress }),
    [rows, pairAddress],
  )

  return (
    <div className="bar tickbar">
      <div className="ticks">
        {items.map((item) => (
          <a
            key={item.key}
            className="tick"
            href={`/trade-v2/solana/${item.key}`}
            title={`${item.ticker} · ${usd(item.capUsd)}`}
          >
            <TokenMark src={item.logo} ticker={item.ticker} size={14} />
            <span className="tk">{item.ticker}</span>
            <span
              className="mono tc"
              style={{
                color: Number.isFinite(item.changePercent)
                  ? changeColour(item.changePercent)
                  : 'var(--t3)',
              }}
            >
              {usd(item.capUsd)}
            </span>
          </a>
        ))}
      </div>

      <div className="right">
        <span className="mono dim">
          {items.length === 0 ? 'reading the market…' : marketLine(items)}
        </span>
      </div>
    </div>
  )
})

const changeColour = (change: number) => {
  if (change === 0) {
    return 'var(--t2)'
  }
  return change > 0 ? 'var(--up)' : 'var(--down)'
}

/**
 * One sentence about the strip itself.
 *
 * A row of caps says what is big, not what is happening. How many of the
 * trending list are up right now is the cheapest honest read of the market's
 * mood, and it costs nothing we have not already fetched.
 */
const marketLine = (
  items: ReadonlyArray<{ changePercent: number }>,
): string => {
  const known = items.filter((item) => Number.isFinite(item.changePercent))
  if (known.length === 0) {
    return ''
  }
  const up = known.filter((item) => item.changePercent > 0).length
  return `${up}/${known.length} up · ${percent((up / known.length) * 100)}`
}
